"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, Sparkles, Mail, Phone, Lock, Clock } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const features = [{
  title: "Direct Access to Yassine",
  description: "You work directly with me—no account managers or junior delegates."
}, {
  title: "Actionable Strategy Insights",
  description: "Real analysis of your website's organic potential and quick wins."
}, {
  title: "Zero Sales Pressure",
  description: "A constructive 30-minute consultation focused entirely on your search growth."
}];
function InnerConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-muted/50 border border-border rounded-xl text-center space-y-4">
        <div className="w-12 h-12 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-bold font-default text-foreground">Consultation Request Received</h3>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
          {successMessage || "Thank you for reaching out. I will review your website details personally and reply within 24 hours to confirm our consultation."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-border hover:bg-accent/10">
          Request Another Time
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Please enter your full name"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="Yassine Essaadi" className="bg-background border-input focus:border-primary px-3 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please enter your email address",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@company.com" className="bg-background border-input focus:border-primary px-3 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Please enter your website URL"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">Website URL</FormLabel>
            <FormControl>
              <Input type="text" placeholder="https://yourwebsite.com" className="bg-background border-input focus:border-primary px-3 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold text-base py-6 rounded-lg transition-colors flex items-center justify-center gap-2">
        {isSubmitting ? <>
            <Clock className="w-5 h-5 animate-spin" />
            <span>Reserving Your Slot...</span>
          </> : <>
            <SafeHydrate><Calendar className="w-5 h-5" /></SafeHydrate>
            <span>Book a Free SEO Consultation</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </>}
      </Button>

      <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5 pt-1">
        <Lock className="w-3.5 h-3.5" />
        <span>Your data is confidential. No spam ever.</span>
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcta" data-nav="light" className="relative py-16 lg:py-24 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted border border-border text-primary text-xs font-medium tracking-wide uppercase">
              <Sparkles className="w-3.5 h-3.5 text-accent" />
              <span>Direct Consultant Experience</span>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight">
                Ready to Grow Your Organic Traffic with Personal Expert Guidance?
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Book a free 30-minute SEO strategy call directly with me. I will personally review your current search visibility, highlight immediate growth opportunities, and show you how a tailored campaign delivers sustainable results.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {features.map((feature, i) => <div data-index={i} key={i} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-card/60 border border-border/50 shadow-xs">
                  <div className="p-1.5 rounded-md bg-primary/10 text-primary mt-0.5 shrink-0">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm mt-0.5">{feature.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4 border-t border-border flex flex-col sm:flex-row sm:items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary shrink-0" />
                <a href="mailto:contact@ye-consultancy.com" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
                  contact@ye-consultancy.com
                </a>
              </div>
              <div className="hidden sm:block text-border">|</div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary shrink-0" />
                <a href="tel:+447490984430" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
                  +44 7490984430
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-xl rounded-2xl overflow-hidden py-0">
              <div className="bg-primary text-primary-foreground p-6 sm:p-8">
                <h3 className="text-xl font-bold font-serif tracking-wide">Request Your Free SEO Audit</h3>
                <p className="text-primary-foreground/80 text-sm mt-1">
                  Fill in your details below to schedule a 1-on-1 strategy session with Yassine.
                </p>
              </div>

              <CardContent className="p-6 sm:p-8 pt-6">
                <FormProvider formId="consultation-cta-form" sectionName="ConsultationCTA" defaultValues={{
                name: "",
                email: "",
                website: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                website: {
                  required: true
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you! Your request has been sent directly to Yassine Essaadi. I will reach out shortly.">
                  <InnerConsultationForm />
                </FormProvider>

                <div className="mt-6 pt-6 border-t border-border text-center">
                  <p className="text-xs text-muted-foreground">
                    Prefer to send a direct message or view full service details first?
                  </p>
                  <Button asChild variant="link" className="text-primary hover:text-primary/80 text-sm font-semibold p-0 h-auto mt-2">
                    <Link to="/ContactPage" className="inline-flex items-center gap-1">
                      <span>Visit Full Contact Page</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}